import React, { useEffect } from 'react';
import { parseDesc, listaRequerimientos } from '../hooks/useEventos';
import { fmtHora, fmtFecha, fmtHoraLibre, val, getDescriptivo, normISO } from '../utils/surgeryHelpers';

const BADGE = {
  pending   : { bg: '#e6f9f8', color: '#038f8a', label: 'Pendiente'  },
  confirmada: { bg: '#fffbeb', color: '#d97706', label: 'Confirmada' },
  cancelada : { bg: '#fdf1f2', color: '#e12f3b', label: 'Cancelada'  },
  historico : { bg: '#f3f4f6', color: '#6b7280', label: 'Realizado'  },
};

/* Todos los pares clave=valor de la descripción que no se muestran ya
   de forma destacada arriba, para no perder información. */
const CAMPOS_DESTACADOS = new Set([
  'AppointmentType', 'Medico', 'Paciente', 'Procedimiento', 'Telefono Medico',
  'Anestesiologo', 'Solicitado', 'Duracion', 'Diagnostico', 'TipoCliente',
  'Unidad Hospitalaroa', 'Unidad Hospitalaria', 'RequerimientoEspecial',
  'PacienteHospitalizado',
]);

const SurgeryDetailModal = ({ doc, tipo, onClose, onConfirmar, onCancelar, onEditar, onTiempos, mostrarUnidad }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!doc) return null;

  const ev       = doc?.event || {};
  const summary  = (ev.summary || '').toLowerCase();
  const startIso = ev.start?.dateTime;
  const endIso   = ev.end?.dateTime;
  const fields   = parseDesc(ev.description);

  const esCancelado = summary === 'cancelada';
  const key   = esCancelado ? 'cancelada' : (tipo === 'historico' ? 'historico' : summary || 'pending');
  const badge = BADGE[key] || BADGE.pending;

  // El botón "Realizada" solo aplica una vez que ya pasó la hora de inicio programada
  const yaInicio = startIso && new Date(normISO(startIso)) <= new Date();

  const quirofano     = val(fields['AppointmentType']) || 'Quirófano';
  const medico        = val(fields['Medico']);
  const paciente      = val(fields['Paciente']);
  const procedimiento = val(fields['Procedimiento']);
  const telefono      = val(fields['Telefono Medico']);
  const anestesiologo = val(fields['Anestesiologo']);
  const solicitado    = val(fields['Solicitado']);
  const duracion      = val(fields['Duracion']);
  const diagnostico   = val(fields['Diagnostico']);
  const tipoCliente   = val(fields['TipoCliente']);
  const unidad        = val(fields['Unidad Hospitalaroa']) || val(fields['Unidad Hospitalaria']);
  const requerimientoEspecial = val(ev.requerimientoEspecial) || val(fields['RequerimientoEspecial']);
  const pacienteHospitalizado = val(fields['PacienteHospitalizado']);
  const reqs          = listaRequerimientos(doc);

  // Cualquier otro campo presente en la descripción que no se listó arriba
  const otrosCampos = Object.entries(fields).filter(
    ([k, v]) => !CAMPOS_DESTACADOS.has(k) && val(v)
  );

  const imagenologia = ev.imagenologia || {};
  const materiales    = Array.isArray(ev.materialEspecial) ? ev.materialEspecial : [];
  const equipos       = Array.isArray(ev.rentaEquipos) ? ev.rentaEquipos : [];
  const muestras      = ev.muestrapatologica || {};
  const tiempos       = ev.tiemposQuirurgicos || {};
  const hayTiempos    = Object.values(tiempos).some(Boolean);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px',
          maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ color: '#039490', fontSize: '16px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.4px' }}>
                {quirofano}
              </span>
              <span style={{ background: badge.bg, color: badge.color, borderRadius: '20px', fontSize: '10px', fontWeight: 800, letterSpacing: '.6px', padding: '3px 9px', textTransform: 'uppercase' }}>
                {badge.label}
              </span>
            </div>
            {unidad && <div style={{ color: '#6b7280', fontSize: '12px' }}>{unidad}</div>}
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: '50%', color: '#374151',
              cursor: 'pointer', fontSize: '16px', height: '30px', width: '30px', flexShrink: 0,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', overflowY: 'auto' }}>

          {esCancelado && (
            <div style={{ background: '#fdf1f2', border: '1px solid #f6b8be', borderRadius: '10px', color: '#991b1b', fontSize: '12px', marginBottom: '16px', padding: '10px 12px' }}>
              <strong>✕ Cirugía cancelada</strong>
              {ev.motivoCancelacion && <div style={{ marginTop: '3px' }}>Motivo: {ev.motivoCancelacion}</div>}
            </div>
          )}

          <div style={{ background: '#f9fafb', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '16px', padding: '12px' }}>
            <span style={{ fontSize: '20px', fontWeight: 800, color: '#111827' }}>{fmtHora(startIso)}</span>
            <span style={{ color: '#9ca3af' }}>→</span>
            <span style={{ fontSize: '20px', fontWeight: 800, color: '#111827' }}>{fmtHora(endIso)}</span>
            {duracion && <span style={{ fontSize: '11px', color: '#6b7280' }}>({duracion}h)</span>}
          </div>

          <DetailField label="Fecha" value={fmtFecha(startIso)} />
          {medico        && <DetailField label="Médico"         value={medico}        sub={telefono ? `☏ ${telefono}` : null} />}
          {paciente      && <DetailField label="Paciente"       value={paciente}      sub={tipoCliente} />}
          {procedimiento && <DetailField label="Procedimiento"  value={procedimiento} />}
          {pacienteHospitalizado && <DetailField label="¿Paciente hospitalizado?" value={pacienteHospitalizado === 'Si' ? 'Sí' : pacienteHospitalizado} />}
          {ev.motivoReagendamiento && <DetailField label="Motivo del último reagendamiento" value={ev.motivoReagendamiento} />}
          {mostrarUnidad && unidad && <DetailField label="Unidad hospitalaria" value={`🏥 ${unidad}`} />}
          {diagnostico   && <DetailField label="Diagnóstico"    value={diagnostico} />}
          {anestesiologo && <DetailField label="Anestesiólogo"  value={anestesiologo} />}
          {solicitado    && <DetailField label="Solicitado por" value={solicitado} />}

          {otrosCampos.length > 0 && otrosCampos.map(([k, v]) => (
            <DetailField key={k} label={k} value={v} />
          ))}

          {reqs.length > 0 && (
            <div style={{ marginTop: '14px' }}>
              <SectionTitle>Requerimientos especiales</SectionTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {reqs.map((r) => (
                  <span key={r} style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '20px', color: '#d97706', fontSize: '11px', fontWeight: 700, padding: '4px 10px' }}>
                    ⚠ {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {requerimientoEspecial && (
            <div style={{ marginTop: '14px' }}>
              <SectionTitle>Requerimiento especial</SectionTitle>
              <p style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', color: '#78350f', fontSize: '12px', margin: 0, padding: '8px 10px' }}>
                {requerimientoEspecial}
              </p>
            </div>
          )}

          {(imagenologia.arcoC || imagenologia.rayoxX) && (
            <ListaChips titulo="Imagenología" items={[
              imagenologia.arcoC  && 'Arco C',
              imagenologia.rayoxX && 'Rayos X',
            ].filter(Boolean)} />
          )}

          {materiales.length > 0 && (
            <GridChips titulo="Material especial" items={materiales.map(getDescriptivo).filter(Boolean)} />
          )}

          {equipos.length > 0 && (
            <GridChips titulo="Renta de equipos" items={equipos.map(getDescriptivo).filter(Boolean)} />
          )}

          {(muestras.postOperatorios || muestras.trasOperatorios) && (
            <ListaChips titulo="Muestra patológica" items={[
              muestras.postOperatorios && 'Post-operatoria',
              muestras.trasOperatorios && 'Trans-operatoria',
            ].filter(Boolean)} />
          )}

          {/* Tiempos quirúrgicos — oculto por el momento a petición.
              No se elimina, solo se deja de mostrar. Para reactivar,
              descomentar este bloque. */}
          {/* {(hayTiempos || ev.tipoIngresoCirugia || ev.derechoCirugia || ev.tipoAnestesia || ev.reintervencion || ev.numeroHospitalizacion || ev.observacionTiempos) && (
            <div style={{ marginTop: '16px' }}>
              <SectionTitle>Tiempos quirúrgicos</SectionTitle>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: (hayTiempos || ev.numeroHospitalizacion) ? '10px' : 0 }}>
                {ev.tipoIngresoCirugia && <Tag>{ev.tipoIngresoCirugia}</Tag>}
                {ev.derechoCirugia     && <Tag>Cirugía {ev.derechoCirugia.toLowerCase()}</Tag>}
                {ev.tipoAnestesia      && <Tag>Anestesia {ev.tipoAnestesia === 'Otro' ? (ev.tipoAnestesiaOtro || 'otro') : ev.tipoAnestesia.toLowerCase()}</Tag>}
                {ev.reintervencion     && <Tag color="#e12f3b" bg="#fdf1f2">Re-intervención</Tag>}
                {ev.numeroHospitalizacion && <Tag color="#0582a9" bg="#e8f4f8">Hosp. #{ev.numeroHospitalizacion}</Tag>}
              </div>

              {hayTiempos && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                  {[
                    ['Ingresa a sala',     tiempos.ingresaSala],
                    ['Inicia anestesia',   tiempos.iniciaAnestesia],
                    ['Inicia cirugía',     tiempos.iniciaCirugia],
                    ['Termina cirugía',    tiempos.terminaCirugia],
                    ['Termina anestesia',  tiempos.terminaAnestesia],
                    ['Egresa de sala',     tiempos.egresaSala],
                  ].filter(([, v]) => v).map(([label, v]) => (
                    <div key={label} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '6px 8px' }}>
                      <span style={{ color: '#9ca3af', display: 'block', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
                      <span style={{ color: '#111827', display: 'block', fontSize: '12px', fontWeight: 700 }}>{fmtHoraLibre(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {ev.observacionTiempos && (
                <p style={{ color: '#374151', fontSize: '12px', marginTop: '10px' }}>{ev.observacionTiempos}</p>
              )}
            </div>
          )} */}
        </div>

        {/* Footer con acciones */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '14px 22px', borderTop: '1px solid #f3f4f6' }}>
          {tipo === 'pendientes' && !esCancelado && onConfirmar && (
            <ModalBtn color="#85c100" bg="#f2f9e6" onClick={() => { onConfirmar(doc); onClose(); }}>✓ Confirmar</ModalBtn>
          )}
          {tipo !== 'historico' && !esCancelado && onEditar && (
            <ModalBtn color="#0582a9" bg="#e8f4f8" onClick={() => { onEditar(doc); onClose(); }}>✎ Reagendar</ModalBtn>
          )}
          {tipo !== 'historico' && !esCancelado && onCancelar && (
            <ModalBtn color="#e12f3b" bg="#fdf1f2" onClick={() => { onCancelar(doc); onClose(); }}>✕ Cancelar</ModalBtn>
          )}
          {tipo !== 'pendientes' && !esCancelado && onTiempos && yaInicio && (
            <ModalBtn color="#7c3aed" bg="#f3ecfe" onClick={() => { onTiempos(doc); onClose(); }}>✓ Realizada</ModalBtn>
          )}
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto', background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
              color: '#374151', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: '7px 14px',
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <span style={{ color: '#6b7280', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px', marginBottom: '6px', textTransform: 'uppercase' }}>
    {children}
  </span>
);

const DetailField = ({ label, value, sub }) => (
  <div style={{ marginBottom: '12px' }}>
    <SectionTitle>{label}</SectionTitle>
    <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 600, lineHeight: 1.4 }}>{value}</span>
    {sub && <span style={{ color: '#0582a9', display: 'block', fontSize: '12px', marginTop: '2px' }}>{sub}</span>}
  </div>
);

const ListaChips = ({ titulo, items }) => (
  <div style={{ marginTop: '14px' }}>
    <SectionTitle>{titulo}</SectionTitle>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {items.map((it, i) => (
        <span key={i} style={{ background: '#f3f4f6', borderRadius: '20px', color: '#374151', fontSize: '11px', fontWeight: 600, padding: '4px 10px' }}>
          {it}
        </span>
      ))}
    </div>
  </div>
);

const Tag = ({ children, color = '#374151', bg = '#f3f4f6' }) => (
  <span style={{ background: bg, borderRadius: '20px', color, fontSize: '11px', fontWeight: 700, padding: '4px 10px' }}>
    {children}
  </span>
);

const GridChips = ({ titulo, items }) => (
  <div style={{ marginTop: '14px' }}>
    <SectionTitle>{titulo}</SectionTitle>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
      {items.map((it, i) => (
        <div
          key={i}
          style={{
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
            color: '#374151', fontSize: '12px', fontWeight: 600, lineHeight: 1.3,
            padding: '8px 10px', textAlign: 'center',
          }}
        >
          {it}
        </div>
      ))}
    </div>
  </div>
);

const ModalBtn = ({ children, color, bg, onClick }) => (
  <button onClick={onClick} style={{ alignItems: 'center', background: bg, border: `1px solid ${color}`, borderRadius: '8px', color, cursor: 'pointer', display: 'inline-flex', fontSize: '11px', fontWeight: 700, gap: '4px', padding: '7px 12px' }}>
    {children}
  </button>
);

export default SurgeryDetailModal;